/**
 * TalentPoolPage — the recruiter-facing talent pool list.
 *
 * Layout (canonical page chrome, matching Dashboard / Jobs / Integrations):
 *   [Header: title + subtitle | toolbar (global quick-search + Add Talent)]
 *   [AppliedFiltersBar — removable pills for active column filters]
 *   [DataTableOrganism: fillHeight table — sticky header, scrolling body,
 *    pinned pagination; every column sortable + filterable via the generic
 *    column-driven filter framework]
 *
 * Filtering strategy:
 *   - Global quick-search (header): accent-insensitive substring over name + all
 *     skill names, wired as the DataTable global filter (fullName + skillsText).
 *   - Per-column filters: TanStack `columnFilters` is the single source of truth,
 *     owned here (controlled) so the header popovers and AppliedFiltersBar share it.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Users, Plus } from 'lucide-react';
import type { Row, ColumnFiltersState } from '@tanstack/react-table';
import { DataTableOrganism } from '@/components/ui/organisms';
import { IllustratedEmptyState, AppliedFiltersBar } from '@/components/ui/molecules';
import { Skeleton } from '@/components/ui/atoms/Skeleton';
import { Button } from '@/components/ui/atoms/Button';
import { SearchInput } from '@/components/ui/search-input';
import { PaginationBar } from '@/components/ui';
import { useTalents, talentKeys } from '@/hooks/useTalentPool';
import { useDeleteTalent } from '@/hooks/useTalentMutations';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { AddTalentModal } from '@/components/talent-pool/AddTalentModal';
import { foldAccents } from '@/components/ui/molecules/ColumnFilter';
import {
  mapTalentToRow,
  createTalentPoolTableColumns,
  buildTalentPoolFilterSpecs,
  type TalentPoolTableRow,
  type TalentPoolColumnFilterData,
} from '@/components/talent-pool/talentPoolTable.columns';

// Rows per UI page. This is a display choice only — the table paginates
// client-side over the pool already loaded in full by useTalents() (see
// listTalents()'s page-walking loop in talentPool.service.ts), so it is
// intentionally independent of the backend's fetch page size (100) and
// carries no network cost when the recruiter clicks between pages.
const TALENT_TABLE_PAGE_SIZE = 10;

// ── Loading skeleton (8 rows matching TalentRow height/layout) ────────────────

function TalentPoolSkeleton() {
  return (
    <div className="flex flex-col divide-y divide-light-lila/30 rounded-lg border border-light-lila/40">
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton.Row key={i} />
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const TalentPoolPage: React.FC = () => {
  const { t } = useTranslation('talentPool');
  const navigate = useNavigate();

  // Global quick-search (header) — composes with column filters.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Per-column filters — single source of truth shared by headers + applied bar.
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Add-talent-via-upload: reuses the resume upload modal. Uploaded CVs are
  // promoted into the tenant talent pool on the backend (async), so refresh the
  // list when the modal closes.
  const queryClient = useQueryClient();
  const [showUpload, setShowUpload] = useState(false);
  const handleUploadClose = () => {
    setShowUpload(false);
    void queryClient.invalidateQueries({ queryKey: talentKeys.lists() });
  };

  // Per-row delete: a trash action in each row opens a danger confirm dialog.
  const [talentToDelete, setTalentToDelete] = useState<TalentPoolTableRow | null>(
    null,
  );
  const deleteMutation = useDeleteTalent(talentToDelete?.id ?? '');
  const handleConfirmDelete = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => setTalentToDelete(null),
    });
  };

  // Fetch all talents (filtering is applied in-table).
  const { data: talents, isLoading, error } = useTalents();

  // ── Map to rows ──────────────────────────────────────────────────────────────
  const allRows = useMemo(
    () => (talents ?? []).map(mapTalentToRow),
    [talents],
  );

  // ── Global quick-search — accent-insensitive over name + all skill names.
  //    Applied before the table so it composes with the per-column filters. ──────
  const searchedRows = useMemo(() => {
    const q = foldAccents(debouncedSearch.trim());
    if (!q) return allRows;
    return allRows.filter(
      (row) =>
        foldAccents(row.fullName).includes(q) ||
        foldAccents(row.skillsText).includes(q),
    );
  }, [allRows, debouncedSearch]);

  // ── Derive filter options/ranges from the full dataset ───────────────────────
  const filterData: TalentPoolColumnFilterData = useMemo(() => {
    const skillSet = new Set<string>();
    const groupSet = new Set<string>();
    let maxYoe = 0;
    let maxJobs = 0;
    for (const row of allRows) {
      for (const s of row.allSkills) skillSet.add(s);
      for (const g of row.groups) groupSet.add(g);
      if (row.totalYoe > maxYoe) maxYoe = row.totalYoe;
      if (row.jobsCount > maxJobs) maxJobs = row.jobsCount;
    }
    return {
      skillOptions: Array.from(skillSet).sort((a, b) => a.localeCompare(b)),
      groupOptions: Array.from(groupSet).sort((a, b) => a.localeCompare(b)),
      maxYoe: Math.max(Math.ceil(maxYoe), 1),
      maxJobs: Math.max(maxJobs, 1),
    };
  }, [allRows]);

  // ── Column defs (stable wrt t + derived filter data) ─────────────────────────
  const columns = useMemo(
    () => createTalentPoolTableColumns({ t, filterData, onDeleteTalent: setTalentToDelete }),
    [t, filterData],
  );

  // Specs map for the AppliedFiltersBar (same source the columns consume).
  const { specs } = useMemo(
    () => buildTalentPoolFilterSpecs(t, filterData),
    [t, filterData],
  );

  // ── Empty state ───────────────────────────────────────────────────────────────
  const emptyState = useMemo(
    () => (
      <div className="flex flex-col items-center gap-5">
        <IllustratedEmptyState
          icon={<Users />}
          title={t('pool.emptyHeading')}
          description={t('pool.emptyBody')}
        />
        <Button
          label={t('pool.addTalent')}
          icon={<Plus/>}
          type="contained"
          intent="secondary"
          onClick={() => setShowUpload(true)}
        />
      </div>
    ),
    [t],
  );

  // ── Row click handler ─────────────────────────────────────────────────────────
  const handleRowClick = (row: Row<TalentPoolTableRow>) => {
    navigate(`/talent-pool/${row.original.id}`);
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="bg-background-gray h-full flex flex-col overflow-hidden">
      {/* Page header — title + toolbar, aligned with Jobs / Integrations / Settings. */}
      <div className="shrink-0 px-6 lg:px-10 pt-6 md:pt-10 pb-5">
        <div className="flex items-center md:items-end justify-between gap-2 md:gap-3">
          <div className="min-w-0 flex-1 md:flex-initial">
            <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight">
              {t('pool.title')}
            </h4>
            <p className="hidden md:block mt-1 text-sm text-dark-invirtus/60 font-body">
              {t('pool.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t('pool.searchPlaceholder')}
              debounceDelay={300}
              onDebouncedChange={setDebouncedSearch}
              showClearButton
            />
            <span aria-hidden className="h-6 w-px bg-light-lila/60 mx-0.5" />
            <Button
              label={t('pool.addTalent')}
              labelClassName="hidden md:inline"
              icon={<Plus/>}
              type="contained"
              intent="secondary"
              title={t('pool.addTalent')}
              onClick={() => setShowUpload(true)}
            />
          </div>
        </div>
      </div>

      {/* Body — applied-filters bar + fill-height table region. */}
      <div className="flex-1 min-h-0 px-6 lg:px-10 pb-6 md:pb-10 flex flex-col gap-3">
        {isLoading ? (
          <TalentPoolSkeleton />
        ) : error ? (
          <IllustratedEmptyState
            icon={<Users />}
            title={t('pool.emptyHeading')}
            description={error.message}
          />
        ) : (
          <>
            <AppliedFiltersBar
              className="shrink-0"
              columnFilters={columnFilters}
              specs={specs}
              onChange={setColumnFilters}
              labels={{
                clearAll: t('pool.filter.clearAll'),
                removeAria: t('pool.filter.removeAria'),
              }}
            />
            <div className="flex-1 min-h-0">
              <DataTableOrganism
                columns={columns}
                data={searchedRows}
                pageSize={TALENT_TABLE_PAGE_SIZE}
                fillHeight
                enableSelection
                enableColumnVisibility={false}
                enableSearch={false}
                columnFilters={columnFilters}
                onColumnFiltersChange={setColumnFilters}
                // Same pagination widget as the job listing (PaginationBar).
                paginationFooterSlot={(table) => (
                  <PaginationBar
                    currentPage={table.getState().pagination.pageIndex + 1}
                    totalPages={table.getPageCount() || 1}
                    totalItems={table.getFilteredRowModel().rows.length}
                    onPageChange={(page) => table.setPageIndex(page - 1)}
                    itemLabel={t('pool.paginationLabel')}
                  />
                )}
                dataTableProps={{
                  emptyState,
                  onRowClick: handleRowClick,
                  // Tenant-defined "group" column is hidden by default (still in the
                  // model); assigning/showing groups is a later iteration.
                  initialColumnVisibility: { groups: false },
                }}
              />
            </div>
          </>
        )}
      </div>

      {showUpload && <AddTalentModal onClose={handleUploadClose} />}

      <ConfirmationDialog
        isOpen={!!talentToDelete}
        onClose={() => setTalentToDelete(null)}
        title={t('detail.deleteConfirmTitle')}
        message={t('detail.deleteConfirmBody', { name: talentToDelete?.fullName ?? '' })}
        buttons={[
          {
            label: t('detail.deleteCancel'),
            onClick: () => setTalentToDelete(null),
            type: 'outlined',
            intent: 'secondary',
            disabled: deleteMutation.isPending,
          },
          {
            label: t('detail.deleteConfirmAction'),
            onClick: handleConfirmDelete,
            type: 'contained',
            intent: 'danger',
            loading: deleteMutation.isPending,
            disabled: deleteMutation.isPending,
          },
        ]}
      />
    </div>
  );
};

export default TalentPoolPage;
